import '@sass/common/theme.scss';
import CwLoadModules from '@customjs/module-loader.js';
import CwThemeFramework from '@customjs/generalFns.js';
import { MatchHeight } from 'js-match-height';

/**
 * Running mixed functions across the theme
 */
CwThemeFramework.generalFns();

/**
 * Bootstrap Tabs
 */
const tabs = document.querySelector('[role="tablist"]');

if (tabs) CwLoadModules.Tabs();

/**
 * Desktop nav hover dropdowns
 * Safari does not reliably apply :hover to non-interactive <li> items,
 * and Bootstrap's click/Popper dropdown fights CSS hover.
 */
function initDesktopNavHover() {
    const nav = document.querySelector('#nav-desktop');
    if (!nav) return;

    nav.querySelectorAll('.dropdown').forEach((item) => {
        const toggle = item.querySelector(':scope > .dropdown-toggle');
        if (toggle) {
            toggle.removeAttribute('data-bs-toggle');
            toggle.addEventListener('click', (event) => {
                event.preventDefault();
            });
        }

        item.addEventListener('mouseenter', () => {
            item.classList.add('is-open');
        });
        item.addEventListener('mouseleave', () => {
            item.classList.remove('is-open');
        });
        item.addEventListener('touchstart', () => {}, { passive: true });
    });
}

initDesktopNavHover();

/**
 * Centers the open mega panel and switches its columns.
 * Returns immediately when the panel is not in the nav.
 */
function initMegaMenu() {
    const menus = document.querySelectorAll('#nav-desktop .nav-mega');
    if (!menus.length) return;

    menus.forEach((menu) => {
        const items = [...menu.querySelectorAll('.nav-mega__locations > li')];
        if (!items.length) return;

        const place = () => {
            const panel = menu.querySelector(':scope > .dropdown-menu');
            if (!panel) return;

            const header = menu.closest('.header') || menu.closest('header');
            if (header) {
                const gap = header.getBoundingClientRect().bottom - menu.getBoundingClientRect().bottom;
                panel.style.top = `calc(100% + ${Math.max(0, gap)}px)`;
                panel.style.setProperty('--mega-bridge', `${Math.max(0, gap) + 4}px`);
            }

            if (!panel.querySelector('.nav-mega__detail.is-active')) {
                panel.style.left = '';
                return;
            }

            const panelWidth = panel.getBoundingClientRect().width;
            if (panelWidth < 1) return;

            const itemLeft = menu.getBoundingClientRect().left;
            const viewportLeft = Math.max(16, (window.innerWidth - panelWidth) / 2);
            panel.style.left = `${Math.round(viewportLeft - itemLeft)}px`;
        };

        const offices = [...menu.querySelectorAll('.nav-mega__office')];

        const activate = (item) => {
            items.forEach((entry) => {
                const on = entry === item;
                entry.classList.toggle('is-active', on);
                const link = entry.querySelector('a');
                if (link) link.setAttribute('aria-expanded', on ? 'true' : 'false');
                const panelId = link ? link.getAttribute('aria-controls') : '';
                const detail = panelId ? document.getElementById(panelId) : null;
                if (detail) detail.classList.toggle('is-active', on);
                offices.forEach((office) => {
                    if (office.getAttribute('data-mega-for') === panelId) {
                        office.classList.toggle('is-active', on);
                    }
                });
            });
            place();
        };

        menu.addEventListener('mouseenter', () => activate(items[0]));
        menu.addEventListener('focusin', place);
        window.addEventListener('resize', place);

        items.forEach((item) => {
            item.addEventListener('mouseenter', () => activate(item));
            const link = item.querySelector('a');
            if (link) link.addEventListener('focus', () => activate(item));
        });

        const list = menu.querySelector('.nav-mega__locations');
        if (!list) return;

        list.addEventListener('keydown', (event) => {
            if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
            const links = items.map((item) => item.querySelector('a')).filter(Boolean);
            const current = links.indexOf(document.activeElement);
            if (current < 0) return;
            event.preventDefault();
            const next = event.key === 'ArrowDown'
                ? (current + 1) % links.length
                : (current - 1 + links.length) % links.length;
            links[next].focus();
        });
    });
}

initMegaMenu();

/**
 * Dropdown
 */
const dropdown = document.querySelector('[data-bs-toggle="dropdown"]');

if (dropdown) CwLoadModules.Dropdown();

/**
 * Accordions
 */
const accordion = document.querySelector('.accordion');

if (accordion) CwLoadModules.Collapse();

/**
 * Service Worker
 */

// check if service worker is available

if ('serviceWorkerNO' in navigator) {
    document.addEventListener('DOMContentLoaded', () => {
        // Registering Service Worker
        navigator.serviceWorker
            // eslint-disable-next-line no-undef
            .register(`${ThemeURLS.childThemeURL}/sw.php`, {
                scope: '/',
            })
            .then((reg) => console.log(reg.scope))
            .catch((error) => console.log(error.message));
    });
}

/**
 * Loading Mobile Menu
 */

function matchMobile() {
    const mobileMediaQuery = window.matchMedia('(max-width: 1200px)');
    return mobileMediaQuery.matches;
}

matchMobile() ? CwThemeFramework.mobileMenu() : '';

window.addEventListener('resize', () => {
    matchMobile() ? CwThemeFramework.mobileMenu() : '';
});

/**
 * Loading Modals in the Page
 * We will be pulling all the buttons with the data-cw-target
 * Attribute in order to initialize all the modals.
 */

// Checking for all out buttons
const modals = document.querySelectorAll('[data-cw-target]');
if (modals.length) {
    const modalInstances = [];
    CwLoadModules.modals().then((CwModals) => {
        modals.forEach((modalBtn, index) => {
            modalInstances[index] = new CwModals('.cw-modal');
            modalInstances[index].init();
        });
    });
}

/**
 * Loading Swiper JS
 * https://swiperjs.com/
 */
const carouselModules = {
    'results-slider': 'results-carousel',
    'results-slider__sidebar': 'results-slider-sidebar',
    'awards-wrap': 'awards-carousel',
    'awards-wrap-sidebar': 'awards-carousel-sidebar',
    'testimonials-section-slider': 'testimonials-carousel',
    'testimonials-section-slider-sidebar': 'testimonials-carousel-sidebar',
    'team-slider': 'team-carousel',
    'grid-videos': 'videos-carousel',
    'attorney-sidebar-slider': 'team-sidebar-widget',
    'offices-slider': 'offices-carousel',
    'testimonials-video-section-slider': 'testimonials-video-carousel',
    'testimonials-video-sidebar-slider': 'testimonials-video-carousel-sidebar',
    'community-video-slider': 'community-video-carousel',
};

// eslint-disable-next-line no-restricted-syntax
for (const [key, value] of Object.entries(carouselModules)) {
    const name = document.getElementById(key);
    if (name) {
        CwLoadModules.swiperSliders(value).then((module) => {
            module.default();
        });
    }
}

/**
 * Loading MatchHeight
 * Create new instance of MatchHeight [new MatchHeight()]
 * for elements that need to have the same height
 * https://www.npmjs.com/package/js-match-height
 */

const testimonialItem = new MatchHeight('.testimonials-section__item');
const whychooseusItem = new MatchHeight('.why-choose-us__item');

/**
 * Smooth Scroll
 * add class '.scroll' to new elements
 * class '.scroll' already added to footer back to top and TOC dynamic link elements
 */
// smooth scroll
document.addEventListener(
    'click',
    (event) => {
        if (!event.target.closest('.scroll')) return;

        const hashValue = event.target.closest('.scroll').getAttribute('href');
        const target = document.querySelector(hashValue);

        target.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
        history.pushState(null, null, hashValue);
        event.preventDefault();
    },
    false
);

/**
 * Load More
 */
const loadMore = null || document.querySelector('.load-more');
const categoryFilter = null || document.querySelector('.cw-filter');

if (loadMore || categoryFilter) {
    const enablePagination = loadMore.dataset.pagination;

    CwLoadModules.AjaxCalls().then((cwAjaxLib) => {
        const ajaxCalls = new cwAjaxLib({
            enablePagination: enablePagination,
        });
        ajaxCalls.init();
        if (categoryFilter) ajaxCalls.filter(categoryFilter);
    });
}